/**
 * Owns the refresh scheduler for a dashboard tab (S3). Eligibility = the tab
 * is the active workspace tab AND the dashboard is the one shown in it
 * (hidden tabs stay mounted, so `isTabActive` — not unmount — drives pause).
 * The scheduler's only outlet is a TanStack refetch; the queryFn is the
 * single fetch path. On tab CLOSE (unmount) the tile cache and runtime state
 * are dropped; in-tab dashboard switches retain both (S13/S15 contract).
 *
 * `onDeckDashboardId`/`onDeckTiles` (S13): a playlist's next dashboard also
 * registers into this SAME scheduler instance and is marked eligible during
 * the pre-warm lead window — so an interval tile that was paused (ineligible)
 * from an earlier visit catches up immediately once it comes on deck, and
 * the per-connection concurrency cap accounts for both dashboards' tiles.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createRefreshScheduler,
  schedulerEntryKey,
  type RefreshScheduler,
} from '../services/refreshScheduler';
import { BACKGROUND_ALERT_MAX_TILES } from '../services/alertEvaluator';
import { flushDashboardRuntimeSummary } from '../services/dashboardRuntimeTelemetry';
import {
  refetchCurrentDashboardTile,
  evictAllDashboardTileQueries,
} from '../services/dashboardTileCache';
import { isTileRunning, useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';

/** Re-register only when execution-relevant tile config changes — a layout
 * drag (new tiles array, same values) must not reset interval timers. */
const tileSignature = (dashboardId: string, tile: Extract<DashboardTile, { connectionId: string }>) =>
  [
    dashboardId,
    tile.connectionId,
    tile.database ?? '',
    tile.source.sql,
    JSON.stringify(tile.refresh),
    tile.tileType === 'stat' ? JSON.stringify(tile.alertRule ?? null) : '',
  ].join('|');

const EMPTY_ON_DECK_TILES: DashboardTile[] = [];

export function useDashboardScheduler(
  dashboardId: string | null,
  tiles: DashboardTile[],
  isTabActive: boolean,
  onDeckDashboardId: string | null = null,
  onDeckTiles: DashboardTile[] = EMPTY_ON_DECK_TILES,
): RefreshScheduler {
  const queryClient = useQueryClient();
  const eligibleDashboardRef = useRef<string | null>(null);
  const onDeckDashboardIdRef = useRef<string | null>(null);
  const listenersRef = useRef(new Set<() => void>());
  // Keyed by the composite scheduler key (A6) — the shown and on-deck
  // dashboards can carry tiles with the same id, so a tile-id-only map would
  // drop one of them. Each value keeps its dashId/tileId so a stale entry can
  // be unregistered addressably.
  const signaturesRef = useRef(new Map<string, { dashId: string; tileId: string; sig: string }>());
  const schedulerRef = useRef<RefreshScheduler | null>(null);

  if (!schedulerRef.current) {
    schedulerRef.current = createRefreshScheduler({
      refetchTile: (dashId, tileId) => {
        void refetchCurrentDashboardTile(queryClient, dashId, tileId);
      },
      maxConcurrentPerConnection: 2,
      isDashboardEligible: (dashId) =>
        eligibleDashboardRef.current === dashId || onDeckDashboardIdRef.current === dashId,
      onEligibilityChange: (cb) => {
        listenersRef.current.add(cb);
        return () => {
          listenersRef.current.delete(cb);
        };
      },
      // S12 reconciliation: the plan's canonical S3 spec + the S12
      // AlertRuleDialog copy ("Always, up to every 60s") both say 60 — this
      // shipped as 30 in S3; realigned here (no other file pins 30 as a
      // production default, only refreshScheduler.test.ts's own
      // self-contained fixture, which exercises the generic engine at an
      // arbitrary budget and is unaffected).
      backgroundBudget: { maxTiles: BACKGROUND_ALERT_MAX_TILES, minIntervalSeconds: 60 },
      isTileRunning,
    });
  }

  useEffect(() => {
    eligibleDashboardRef.current = isTabActive ? dashboardId : null;
    onDeckDashboardIdRef.current = onDeckDashboardId;
    for (const cb of [...listenersRef.current]) cb();
  }, [dashboardId, isTabActive, onDeckDashboardId]);

  // Register the shown dashboard's query tiles, PLUS the playlist's on-deck
  // dashboard's (S13) — both live on this one scheduler instance so the
  // per-connection cap and eligibility-driven catch-up see both together.
  // Drop tiles that are no longer in either set (removed/switched-away).
  useEffect(() => {
    const scheduler = schedulerRef.current!;
    const current = new Map<string, { dashId: string; tile: DashboardTile; sig: string }>();
    const collect = (dashId: string | null, tileList: DashboardTile[]) => {
      if (!dashId) return;
      for (const tile of tileList) {
        if (isQueryBackedTile(tile)) {
          current.set(schedulerEntryKey(dashId, tile.id), {
            dashId,
            tile,
            sig: tileSignature(dashId, tile),
          });
        }
      }
    };
    collect(dashboardId, tiles);
    collect(onDeckDashboardId, onDeckTiles);
    for (const [key, prev] of [...signaturesRef.current]) {
      if (!current.has(key)) {
        scheduler.unregister(prev.dashId, prev.tileId);
        signaturesRef.current.delete(key);
      }
    }
    for (const [key, { dashId, tile, sig }] of current) {
      if (signaturesRef.current.get(key)?.sig !== sig) {
        scheduler.register(dashId, tile);
        signaturesRef.current.set(key, { dashId, tileId: tile.id, sig });
      }
    }
  }, [dashboardId, tiles, onDeckDashboardId, onDeckTiles]);

  // Tab close: stop everything and drop tile cache + runtime state. Flush
  // the aggregated run-outcome counters so a short-lived dashboard session
  // still reports its runs (the 5-min interval alone would drop them).
  useEffect(
    () => () => {
      schedulerRef.current?.dispose();
      evictAllDashboardTileQueries(queryClient);
      useDashboardRuntimeStore.setState({ byTile: {} });
      flushDashboardRuntimeSummary();
    },
    [queryClient],
  );

  return schedulerRef.current;
}
